import { invokeWithRetry } from "./common/invokeWithRetry";

/**
 * Réinitialisation de mot de passe par code propriétaire.
 *
 * Ces appels sont faits depuis une page publique, SANS session : d'où
 * `skipSessionHandling` (un 401 ne doit ni déconnecter ni rediriger).
 */

export type ResetTokenError =
  | "invalid_token"
  | "expired"
  | "already_used"
  | "too_many_attempts"
  | "invalid_password"
  | "server_error";

interface ValidateResponse {
  success: boolean;
  valid?: boolean;
  email?: string;
  error?: string;
}

export interface ValidateTokenResult {
  valid: boolean;
  email?: string;
  reason?: ResetTokenError;
}

export interface ConsumeTokenResult {
  success: boolean;
  reason?: ResetTokenError;
  message?: string;
}

const asReason = (value: unknown): ResetTokenError =>
  typeof value === "string" &&
  ["invalid_token", "expired", "already_used", "too_many_attempts", "invalid_password"].includes(
    value
  )
    ? (value as ResetTokenError)
    : "server_error";

export async function validateResetToken(token: string): Promise<ValidateTokenResult> {
  const { data, error } = await invokeWithRetry<ValidateResponse>(
    "password-reset-token",
    { action: "validate", token },
    2,
    { skipSessionHandling: true, retryOnServerError: true, timeoutMs: 15000 }
  );

  if (error && !data) {
    console.error("[passwordReset] validate failed:", error);
    return { valid: false, reason: "server_error" };
  }

  if (data?.valid) {
    return { valid: true, email: data.email };
  }

  return { valid: false, reason: asReason(data?.error) };
}

export async function consumeResetToken(
  token: string,
  password: string
): Promise<ConsumeTokenResult> {
  const { data, error } = await invokeWithRetry<ValidateResponse>(
    "password-reset-token",
    { action: "consume", token, password },
    1,
    { skipSessionHandling: true, timeoutMs: 20000 }
  );

  if (data?.success) {
    return { success: true };
  }

  if (error && !data) {
    console.error("[passwordReset] consume failed:", error);
    return { success: false, reason: "server_error" };
  }

  return { success: false, reason: asReason(data?.error), message: data?.error };
}
